import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Frown, Smile } from "lucide-react";
import { Reveal, SectionHeader } from "./SectionHeader";

const EXAMPLES = [
  {
    id: "dependencia",
    label: "Carta de dependencia",
    title: "La carta de una ayuda social",
    before:
      "Vista la resolución del expediente instruido al amparo de la Ley 39/2006, de 26 de diciembre, de Promoción de la Autonomía Personal y Atención a las Personas Dependientes, y de conformidad con lo establecido en el artículo 27.3 de la citada norma, se procede a la notificación de la presente resolución, contra la cual cabe interponer recurso de alzada en el plazo de un mes.",
    after: [
      "Le contamos el resultado de su solicitud de ayuda.",
      "La respuesta es SÍ: usted puede recibir la ayuda.",
      "Cada mes recibirá una cantidad de dinero.",
      "Si no está de acuerdo, puede reclamar en un mes.",
      "Si tiene dudas, pregunte en su centro de servicios sociales.",
    ],
  },
  {
    id: "cita-medica",
    label: "Cita médica",
    title: "Una cita con el médico",
    before:
      "Se procede a citar a usted a fin de que acuda al dispositivo asistencial que se indica, en turno de mañana, previa confirmación telefónica, a efectos de realización de prueba diagnóstica por imagen, debiendo acudir en ayunas de seis horas y aportando la documentación clínica de la que disponga.",
    after: [
      "Tiene una cita con el médico.",
      "Cuándo: el día 14 de mayo, por la mañana.",
      "No debe comer nada durante las 6 horas antes de la cita.",
      "Lleve con usted sus informes médicos anteriores.",
      "Si no puede venir, llame a su centro de salud.",
    ],
  },
  {
    id: "alquiler",
    label: "Contrato de alquiler",
    title: "Un contrato de alquiler",
    before:
      "El arrendatario se obliga al abono de la renta mensual dentro de los primeros cinco días naturales de cada mes, sin perjuicio de la actualización anual de la renta conforme al índice que resulte de aplicación, constituyendo el impago reiterado causa de resolución del presente contrato.",
    after: [
      "Si alquilas una vivienda, estas son tus obligaciones.",
      "Pagas el alquiler dentro de los 5 primeros días de cada mes.",
      "El precio puede subir un poco cada año. Lo dice la ley.",
      "Si no pagas varios meses, el dueño puede terminar el contrato.",
    ],
  },
];

const longest = (t) =>
  Math.max(
    ...t
      .split(/[.!?\n]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => s.split(/\s+/).length)
  );

export default function Comparator() {
  const [active, setActive] = useState(EXAMPLES[0]);

  return (
    <section id="comparador" className="py-20 md:py-28 bg-[#f3ecdc] border-y-2 border-[var(--ink)]/10">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <SectionHeader
          eyebrow="Antes y después"
          title="Lo mismo, contado de dos maneras."
          text="Elige un documento y compara. El texto original y su versión en lectura fácil."
        />

        <Reveal delay={0.1} className="mt-12">
          <div className="flex flex-wrap gap-3" role="tablist" aria-label="Elegir documento de ejemplo">
            {EXAMPLES.map((ex) => (
              <button
                key={ex.id}
                role="tab"
                aria-selected={active.id === ex.id}
                data-testid={`comparator-tab-${ex.id}`}
                onClick={() => setActive(ex)}
                className={`sketch-sm px-5 py-2.5 text-sm md:text-base font-bold transition-all ${
                  active.id === ex.id ? "bg-[var(--yellow)] shadow-[3px_3px_0_var(--ink)] -translate-y-0.5" : "bg-[var(--card)] hover:-translate-y-0.5"
                }`}
              >
                {ex.label}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="grid md:grid-cols-2 gap-8 mt-8"
            data-testid="comparator-visual"
          >
            <div className="sketch bg-[#efe9df] p-7 md:p-8 flex flex-col" data-testid="comparator-before">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-[var(--muted)]">Antes · Texto difícil</p>
                <p className="inline-flex items-center gap-1.5 text-xs font-bold bg-[var(--card)] sketch-sm px-3 py-1">
                  <Frown className="w-4 h-4" aria-hidden="true" />
                  Frase más larga: {longest(active.before)} palabras
                </p>
              </div>
              <h3 className="font-display font-bold text-xl mt-4">{active.title}</h3>
              <p className="mt-4 text-base leading-[1.65] text-[#3d4148] text-justify">{active.before}</p>
            </div>

            <div className="sketch-2 bg-[var(--card)] p-7 md:p-8 flex flex-col shadow-[6px_6px_0_var(--ink)]" data-testid="comparator-after">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-[var(--blue-deep)]">Después · Lectura fácil</p>
                <p className="inline-flex items-center gap-1.5 text-xs font-bold bg-[var(--yellow)] sketch-sm px-3 py-1">
                  <Smile className="w-4 h-4" aria-hidden="true" />
                  Frase más larga: {longest(active.after.join(" "))} palabras
                </p>
              </div>
              <h3 className="font-display font-bold text-xl mt-4">{active.title}</h3>
              <div className="mt-4 space-y-3">
                {active.after.map((line) => (
                  <p key={line} className="flex items-start gap-3 text-lg leading-relaxed">
                    <span className="mt-2 w-2.5 h-2.5 shrink-0 bg-[var(--blue)]" aria-hidden="true" />
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <p className="mt-6 text-sm text-[var(--muted)] italic">Ejemplos creados con fines ilustrativos.</p>
      </div>
    </section>
  );
}
